/**
 * iter70 — Mobile-only bottom action bar for the public gallery.
 *
 * Four quick actions:
 *   1. Share  — opens the Web Share API sheet OR the QR modal fallback.
 *   2. My photos — triggers the guest onboarding wizard.
 *   3. Locked  — deep-links to the studio's WhatsApp with a pre-filled
 *                "I want the locked photos" message.
 *   4. Download — opens the bulk-ZIP scope-picker modal.
 *
 * Hidden on md+ screens per product decision (Samaro-style is mobile-first).
 */

import { Download, Heart, Lock, QrCode, Share2 } from "lucide-react";

const STUDIO_WHATSAPP = "918055502966"; // iter111 — Official studio contact

function lockedPhotosLink(coupleName) {
  const couple = (coupleName || "your event").trim();
  const text = `Hi Rakesh! I'd like access to the locked / private photos from ${couple}. Please share the details.`;
  return `https://wa.me/${STUDIO_WHATSAPP}?text=${encodeURIComponent(text)}`;
}

export default function MobileActionBar({
  coupleName,
  matchCount = 0,
  onShare,
  onFindMe,
  onDownload,
}) {
  const items = [
    {
      key: "share",
      label: "Share",
      icon: Share2,
      testid: "mobile-action-share",
      onClick: onShare,
    },
    {
      key: "photos",
      label: matchCount > 0 ? "My photos" : "Find me",
      icon: Heart,
      testid: "mobile-action-my-photos",
      onClick: onFindMe,
      badge: matchCount > 0 ? matchCount : null,
    },
    {
      key: "locked",
      label: "Locked",
      icon: Lock,
      testid: "mobile-action-locked",
      href: lockedPhotosLink(coupleName),
    },
    {
      key: "download",
      label: "Download",
      icon: Download,
      testid: "mobile-action-download",
      onClick: onDownload,
    },
  ];

  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-stone-200 shadow-[0_-6px_20px_-8px_rgba(0,0,0,0.15)]"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="mobile-action-bar"
    >
      <ul className="flex items-stretch">
        {items.map((it) => {
          const Icon = it.icon;
          const inner = (
            <div className="flex flex-col items-center justify-center gap-1 py-2.5 px-2 text-stone-700 hover:text-rose-700 active:bg-rose-50 transition">
              <div className="relative">
                <Icon className="w-5 h-5" strokeWidth={1.8} />
                {it.badge && (
                  <span className="absolute -top-1.5 -right-2 bg-rose-600 text-white text-[9px] font-bold rounded-full px-1 min-w-[16px] h-4 flex items-center justify-center">
                    {it.badge > 99 ? "99+" : it.badge}
                  </span>
                )}
              </div>
              <span className="text-[10px] font-medium tracking-wide uppercase">{it.label}</span>
            </div>
          );

          return (
            <li key={it.key} className="flex-1 min-w-0">
              {it.href ? (
                <a
                  href={it.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full"
                  data-testid={it.testid}
                >
                  {inner}
                </a>
              ) : (
                <button
                  type="button"
                  onClick={it.onClick}
                  className="block w-full"
                  data-testid={it.testid}
                >
                  {inner}
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {/* Small QR affordance under Share for guests who don't know Web Share API */}
      <button
        type="button"
        onClick={onShare}
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
      >
        <QrCode className="w-4 h-4" />
      </button>
    </nav>
  );
}
